import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import PerformanceFilters from "../performance/PerformanceFilters";
import TeamPerformanceGrid from "../performance/TeamPerformanceGrid";
import ReviewQueue from "../performance/ReviewQueue";
import MemberPerformanceDrawer from "../performance/MemberPerformanceDrawer";
import ManagerPIPView from "../performance/ManagerPIPView";
import ManagerCareerView from "../performance/ManagerCareerView";
import ManagerTransferView from "../performance/ManagerTransferView";
import SalaryCycleControls from "../performance/SalaryCycleControls";
import SalaryReviewGrid from "../performance/SalaryReviewGrid";

const NEON = "#00FF99";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const _now = new Date();
const CURRENT_QUARTER = `Q${Math.ceil((_now.getMonth() + 1) / 3)}-${_now.getFullYear()}`;

export default function TeamPerformancePage() {
  const [managerId, setManagerId] = useState(null);
  useEffect(() => {
    try { const u = JSON.parse(localStorage.getItem("user") || "null"); if (u?.id) setManagerId(u.id); } catch {}
  }, []);

  const [view, setView] = useState("team"); // "team" | "queue"
  const [filters, setFilters] = useState({
    period: CURRENT_QUARTER,
    ratingMin: "ALL",     // ALL | 3 | 4
    status: "ALL",        // ALL | ON_TRACK | AT_RISK
    search: "",
  });

  const [rows, setRows] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { id, name }
  const [salaryCycle, setSalaryCycle] = useState(null); // active salary-review cycle

  useEffect(() => {
    if (!managerId) return;
    let active = true;

    const run = () => {
      // PIP view owns its own data-fetching (ManagerPIPView) — skip the
      // team/queue fetch entirely so it isn't triggered as a side effect.
      if (view !== "team" && view !== "queue") { setLoading(false); return; }
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (view === "team") {
        fetch(`${API_BASE}/api/performance/team`, { headers })
          .then(r => r.json())
          .then(d => {
            if (!active) return;
            const data = Array.isArray(d) ? d : (d?.data || []);
            setRows(data.map(r => ({
              id:         r.user_id,
              name:       r.name || "",
              role:       r.role || "",
              rating:     r.rating ?? null,
              goals:      {
                done:  r.goals_done  ?? 0,
                total: r.goals_total ?? 0,
              },
              status:     r.status || "ON_TRACK",
              lastReview: r.last_review || null,
              nextReview: r.next_review || null,
              trend:      r.trend || "flat",
              _raw:       r,
            })));
          })
          .catch(() => { if (active) setRows([]); })
          .finally(() => { if (active) setLoading(false); });
      } else {
        fetch(`${API_BASE}/api/performance/team/queue`, { headers })
          .then(r => r.json())
          .then(d => {
            if (!active) return;
            const data = Array.isArray(d) ? d : (d?.data || []);
            setQueue(data.map(r => ({
              id:     r.id || r.user_id,
              userId: r.user_id,
              name:   r.name || "",
              role:   r.role || "",
              due:    r.next_review || "",
              type:   r.stage || "Manager Review",
              status: r.stage || "PENDING",
            })));
          })
          .catch(() => { if (active) setQueue([]); })
          .finally(() => { if (active) setLoading(false); });
      }
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["goal_create", "self_review", "manager_review"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [managerId, view, filters.period]);

  // KPIs
  const kpis = useMemo(() => {
    if (!rows.length) return { avgRating: "—", atRisk: 0, reviewsDue30: 0, avgGoals: "—" };
    const avgRating = (rows.reduce((s, r) => s + r.rating, 0) / rows.length).toFixed(2);
    const atRisk = rows.filter(r => r.status === "AT_RISK").length;
    const today = new Date().toISOString().slice(0,10);
    const in30 = new Date(new Date().setDate(new Date().getDate()+30)).toISOString().slice(0,10);
    const reviewsDue30 = rows.filter(r => r.nextReview && r.nextReview >= today && r.nextReview <= in30).length;
    const avgGoals = Math.round(rows.reduce((s, r) => s + (r.goals.done / Math.max(1, r.goals.total)), 0) / rows.length * 100);
    return { avgRating, atRisk, reviewsDue30, avgGoals: `${avgGoals}%` };
  }, [rows]);

  // Client-side filters/search
  const visibleRows = useMemo(() => {
    let out = [...rows];
    if (filters.ratingMin !== "ALL") out = out.filter(r => r.rating >= Number(filters.ratingMin));
    if (filters.status !== "ALL") out = out.filter(r => r.status === filters.status);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(r => r.name.toLowerCase().includes(q) || (r.role || "").toLowerCase().includes(q));
    }
    return out;
  }, [rows, filters]);

  const isPip = view === "pip";
  const isCareer = view === "career";
  const isTransfer = view === "transfer";
  const isSalary = view === "salary";

  return (
    <MessengerLayout>
      <div className="text-white space-y-6">
        {/* Additive top-level toggle: Performance (team/queue) vs PIP vs Career
            vs Transfers vs Salary Review. Kept out of PerformanceFilters. */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "perf",     label: "Performance",    active: !isPip && !isCareer && !isTransfer && !isSalary, on: () => setView("team") },
            { key: "pip",      label: "PIP Management", active: isPip,      on: () => setView("pip") },
            { key: "career",   label: "Career",         active: isCareer,   on: () => setView("career") },
            { key: "transfer", label: "Transfers",      active: isTransfer, on: () => setView("transfer") },
            { key: "salary",   label: "Salary Review",  active: isSalary,   on: () => setView("salary") },
          ].map(t => (
            <button key={t.key} onClick={t.on}
              className={`text-xs px-4 py-2 rounded-full border transition-colors ${
                t.active
                  ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                  : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {isPip ? (
          <ManagerPIPView />
        ) : isCareer ? (
          <ManagerCareerView />
        ) : isTransfer ? (
          <ManagerTransferView />
        ) : isSalary ? (
          <div className="space-y-5">
            <SalaryCycleControls selectedCycleId={salaryCycle?.id} onSelectCycle={setSalaryCycle} />
            <SalaryReviewGrid cycle={salaryCycle} />
          </div>
        ) : (
          <>
            <PerformanceFilters
              view={view}
              setView={setView}
              filters={filters}
              setFilters={setFilters}
              kpis={kpis}
            />

            <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
              {view === "team" && (
                <TeamPerformanceGrid
                  loading={loading}
                  rows={visibleRows}
                  onRowClick={(r) => setSelected({ id: r.id, name: r.name })}
                />
              )}

              {view === "queue" && (
                <ReviewQueue
                  loading={loading}
                  items={queue}
                  onComplete={(id, note) => {
                    const item = queue.find(i => i.id === id);
                    const userId = item?.userId;
                    if (!userId) return;
                    const token = localStorage.getItem("token");
                    const reviewerId = (() => {
                      try { return JSON.parse(localStorage.getItem("user") || "null")?.id; }
                      catch { return null; }
                    })();
                    fetch(`${API_BASE}/api/performance/team/${userId}/rate`, {
                      method: "POST",
                      headers: {
                        Authorization:  `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        reviewer_id:  reviewerId,
                        achievements: note || "",
                      }),
                    })
                      .then(() => setQueue(q => q.filter(i => i.id !== id)))
                      .catch(() => {});
                  }}
                  onApprove={(id) => {
                    const item = queue.find(i => i.id === id);
                    const userId = item?.userId;
                    if (!userId) return;
                    const token = localStorage.getItem("token");
                    fetch(`${API_BASE}/api/performance/${userId}/stage`, {
                      method: "PATCH",
                      headers: {
                        Authorization:  `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ stage: "Finalize" }),
                    })
                      .then(() => setQueue(q => q.filter(i => i.id !== id)))
                      .catch(() => {});
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>

      {selected && (
        <MemberPerformanceDrawer
          member={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </MessengerLayout>
  );
}
